import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Loader2,
  Sparkles,
  Download,
  ChevronLeft,
  ChevronRight,
  RefreshCcw,
  Layers,
  Pencil,
  Eye,
  Image as ImageIcon,
  XCircle,
  Save,
  Type as TypeIcon,
  Move,
  Palette,
  LayoutTemplate,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import { linkedge } from '../../lib/api';
import {
  isCustomLayout,
  renderCustomLayoutSlide,
  CustomLayoutMiniTile,
} from './CarouselLayouts';
import ColorSwatchPicker from './ColorSwatchPicker';

// ---- 6 visual templates — mirror backend TEMPLATE_PRESETS for live preview ----
const TEMPLATES = {
  bloomberg: {
    label: 'Bloomberg Teal',
    description: 'Dark teal cover, light interiors, amber accent.',
    cover: { bg: '#0B5E58', title: '#FFFFFF', body: '#D9EAE7', kicker: '#F59E0B', accent: '#F59E0B' },
    cta: { bg: '#E8F1EF', title: '#0B5E58', body: '#0F1717', kicker: '#0F766E', accent: '#0F766E' },
    default: { bg: '#F5F7F6', title: '#0F1717', body: '#506662', kicker: '#0F766E', accent: '#0F766E' },
  },
  editorial: {
    label: 'Editorial Cream',
    description: 'Warm cream, deep navy, terracotta accent.',
    cover: { bg: '#1B2845', title: '#FBF7EE', body: '#E4D9C2', kicker: '#D2553C', accent: '#D2553C' },
    cta: { bg: '#FBF7EE', title: '#1B2845', body: '#3D3225', kicker: '#D2553C', accent: '#1B2845' },
    default: { bg: '#FBF7EE', title: '#1B2845', body: '#5C4A33', kicker: '#D2553C', accent: '#D2553C' },
  },
  midnight: {
    label: 'Midnight Terminal',
    description: 'Pure black, white headlines, electric green.',
    cover: { bg: '#0A0A0A', title: '#FFFFFF', body: '#B8B8B8', kicker: '#00E676', accent: '#00E676' },
    cta: { bg: '#111111', title: '#FFFFFF', body: '#D0D0D0', kicker: '#00E676', accent: '#00E676' },
    default: { bg: '#0F0F0F', title: '#FFFFFF', body: '#A8A8A8', kicker: '#00E676', accent: '#00E676' },
  },
  verdant: {
    label: 'Verdant Forest',
    description: 'Deep forest green with mint and gold.',
    cover: { bg: '#1F3D2C', title: '#FAF6E5', body: '#D4D9BD', kicker: '#E8B53B', accent: '#E8B53B' },
    cta: { bg: '#E6F1E2', title: '#1F3D2C', body: '#2E4A38', kicker: '#1F3D2C', accent: '#E8B53B' },
    default: { bg: '#F1F6EB', title: '#1F3D2C', body: '#475C4B', kicker: '#1F3D2C', accent: '#1F3D2C' },
  },
  hotpink: {
    label: 'Hot Pink Pop',
    description: 'Magenta cover, blush interiors, ink-black accent.',
    cover: { bg: '#E91E63', title: '#FFFFFF', body: '#FFEAF1', kicker: '#111111', accent: '#111111' },
    cta: { bg: '#FFEFF4', title: '#111111', body: '#3A1722', kicker: '#E91E63', accent: '#E91E63' },
    default: { bg: '#FFF5F8', title: '#111111', body: '#5C2939', kicker: '#E91E63', accent: '#E91E63' },
  },
  newsprint: {
    label: 'Newsprint',
    description: 'Ivory paper, black serif headlines, vermilion.',
    cover: { bg: '#F8F3E6', title: '#0F0F0F', body: '#3A352B', kicker: '#C8261A', accent: '#C8261A' },
    cta: { bg: '#F8F3E6', title: '#0F0F0F', body: '#3A352B', kicker: '#C8261A', accent: '#C8261A' },
    default: { bg: '#F8F3E6', title: '#0F0F0F', body: '#3A352B', kicker: '#C8261A', accent: '#0F0F0F' },
  },
  editorial_bold: {
    label: 'Editorial Bold',
    description: 'Off-white canvas, bold serif headlines, purple inline emphasis. PDF-only layout.',
    cover: { bg: '#FAFAF7', title: '#0A0A0A', body: '#3F3F46', kicker: '#525252', accent: '#7C3AED' },
    cta: { bg: '#FAFAF7', title: '#0A0A0A', body: '#3F3F46', kicker: '#525252', accent: '#7C3AED' },
    default: { bg: '#FAFAF7', title: '#0A0A0A', body: '#3F3F46', kicker: '#525252', accent: '#7C3AED' },
  },
  index_card: {
    label: 'Index Card',
    description: 'Photo background with floating white text card + large royal-blue numeral. PDF-only layout.',
    cover: { bg: '#1F2937', title: '#FFFFFF', body: '#E5E7EB', kicker: '#FFFFFF', accent: '#2563EB' },
    cta: { bg: '#DDD9D2', title: '#0A0A0A', body: '#374151', kicker: '#2563EB', accent: '#2563EB' },
    default: { bg: '#DDD9D2', title: '#0A0A0A', body: '#374151', kicker: '#2563EB', accent: '#2563EB' },
  },
  portrait_spotlight: {
    label: 'Portrait Spotlight',
    description: 'Vertical image band, dark navy ground, gold accent badge. PDF-only layout.',
    cover: { bg: '#0F172A', title: '#FFFFFF', body: '#CBD5E1', kicker: '#94A3B8', accent: '#EAB308' },
    cta: { bg: '#0F172A', title: '#FFFFFF', body: '#CBD5E1', kicker: '#94A3B8', accent: '#EAB308' },
    default: { bg: '#0F172A', title: '#FFFFFF', body: '#CBD5E1', kicker: '#94A3B8', accent: '#EAB308' },
  },
  pop_frame: {
    label: 'Pop Frame',
    description: 'Saturated yellow + pink, circular image holder, big uppercase. PDF-only layout.',
    cover: { bg: '#FACC15', title: '#0A0A0A', body: '#1F2937', kicker: '#0A0A0A', accent: '#EC4899' },
    cta: { bg: '#FACC15', title: '#0A0A0A', body: '#1F2937', kicker: '#0A0A0A', accent: '#EC4899' },
    default: { bg: '#FACC15', title: '#0A0A0A', body: '#1F2937', kicker: '#0A0A0A', accent: '#EC4899' },
  },
  magazine_mono: {
    label: 'Magazine Mono',
    description: 'Black, white, grey only. Editorial restraint, ruled hairlines. PDF-only layout.',
    cover: { bg: '#FFFFFF', title: '#0A0A0A', body: '#404040', kicker: '#737373', accent: '#0A0A0A' },
    cta: { bg: '#FFFFFF', title: '#0A0A0A', body: '#404040', kicker: '#737373', accent: '#0A0A0A' },
    default: { bg: '#FFFFFF', title: '#0A0A0A', body: '#404040', kicker: '#737373', accent: '#0A0A0A' },
  },
  quote_stack: {
    label: 'Quote Stack',
    description: 'Oversized quote marks, stacked typography, asymmetric attribution. PDF-only layout.',
    cover: { bg: '#F5F5F4', title: '#0A0A0A', body: '#404040', kicker: '#A8A29E', accent: '#DC2626' },
    cta: { bg: '#F5F5F4', title: '#0A0A0A', body: '#404040', kicker: '#A8A29E', accent: '#DC2626' },
    default: { bg: '#F5F5F4', title: '#0A0A0A', body: '#404040', kicker: '#A8A29E', accent: '#DC2626' },
  },
  // ---- Premium HTML/CSS renders (no live recolor, exquisite PDF output) ----
  editorial_spread: {
    label: 'Editorial Spread',
    description: 'PREMIUM · Magazine-style serif headlines, asymmetric grid, drop caps, terracotta accent.',
    premium: true,
    cover: { bg: '#FBF8F1', title: '#161512', body: '#4A443C', kicker: '#B04A23', accent: '#B04A23' },
    cta: { bg: '#161512', title: '#FBF8F1', body: '#E9D6C4', kicker: '#E9D6C4', accent: '#B04A23' },
    default: { bg: '#FBF8F1', title: '#161512', body: '#4A443C', kicker: '#B04A23', accent: '#B04A23' },
  },
  stat_hero: {
    label: 'Stat Hero',
    description: 'PREMIUM · Bold dark canvas, one huge stat per slide, lemon-yellow accent, monospace eyebrows.',
    premium: true,
    cover: { bg: '#0F1116', title: '#E9E7E0', body: '#8B8A85', kicker: '#F7E26B', accent: '#F7E26B' },
    cta: { bg: '#F5F4EF', title: '#0A0A0A', body: '#4A4A4A', kicker: '#0A0A0A', accent: '#FFD400' },
    default: { bg: '#0F1116', title: '#E9E7E0', body: '#8B8A85', kicker: '#F7E26B', accent: '#F7E26B' },
  },
  mono_editorial: {
    label: 'Mono Editorial',
    description: 'PREMIUM · Black-and-white restraint, framed Newsreader serif, italic pull-quotes.',
    premium: true,
    cover: { bg: '#FFFFFF', title: '#0A0A0A', body: '#404040', kicker: '#737373', accent: '#0A0A0A' },
    cta: { bg: '#0A0A0A', title: '#FFFFFF', body: '#D4D4D4', kicker: '#FFFFFF', accent: '#FFFFFF' },
    default: { bg: '#FFFFFF', title: '#0A0A0A', body: '#404040', kicker: '#737373', accent: '#0A0A0A' },
  },
  gradient_modern: {
    label: 'Gradient Modern',
    description: 'PREMIUM · Vivid sunrise gradients, soft glass cards, bold Inter display typography.',
    premium: true,
    cover: { bg: 'linear-gradient(135deg,#ffd9c0 0%,#ffaa83 38%,#c87de6 78%,#6f5ce5 100%)', title: '#0E1116', body: '#2C2F36', kicker: '#0E1116', accent: '#FF5A45' },
    cta: { bg: 'linear-gradient(160deg,#e7e0ff 0%,#b4a4ff 45%,#6c63ff 100%)', title: '#0E1116', body: '#2C2F36', kicker: '#0E1116', accent: '#FF5A45' },
    default: { bg: 'linear-gradient(150deg,#ffe9ad 0%,#ff9d77 50%,#ec5a8d 100%)', title: '#0E1116', body: '#2C2F36', kicker: '#0E1116', accent: '#FF5A45' },
  },
};

const FONT_OPTIONS = [
  { key: 'sans', label: 'Sans (Helvetica)', css: 'sans-serif' },
  { key: 'sans_oblique', label: 'Sans Italic', css: 'sans-serif' },
  { key: 'serif', label: 'Serif (Times)', css: '"Playfair Display", Georgia, serif' },
  { key: 'serif_italic', label: 'Serif Italic', css: '"Playfair Display", Georgia, serif' },
  { key: 'mono', label: 'Mono (Courier)', css: '"Source Code Pro", monospace' },
];

const DEFAULT_LOGO_POSITION = { x: 0.87, y: 0.06, size: 56 };
const DEFAULT_IMAGE_POSITION = { x: 0.5, y: 0.2, scale: 1 };

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function templateRoleColors(templateKey, role) {
  const t = TEMPLATES[templateKey] || TEMPLATES.bloomberg;
  const key = role === 'cover' || role === 'cta' ? role : 'default';
  return t[key] || t.default;
}

function clamp01(n) {
  if (Number.isNaN(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

// Draggable overlay element. Renders children at the given fractional position
// (centre of element). onChange called with {x, y} during drag.
function Draggable({ x, y, sizePx, onChange, locked, children, ariaLabel, testId }) {
  const ref = useRef(null);
  const dragRef = useRef({ active: false, offX: 0, offY: 0 });

  const handleStart = (clientX, clientY) => {
    if (locked) return;
    const parent = ref.current?.parentElement;
    if (!parent) return;
    const rect = parent.getBoundingClientRect();
    const elRect = ref.current.getBoundingClientRect();
    // Offset between cursor and element centre
    const centreX = elRect.left + elRect.width / 2;
    const centreY = elRect.top + elRect.height / 2;
    dragRef.current = {
      active: true,
      offX: clientX - centreX,
      offY: clientY - centreY,
      parentRect: rect,
    };
    document.body.style.userSelect = 'none';
  };
  const handleMove = useCallback((clientX, clientY) => {
    const d = dragRef.current;
    if (!d.active || !d.parentRect) return;
    const parentRect = d.parentRect;
    const nx = (clientX - d.offX - parentRect.left) / parentRect.width;
    const ny = (clientY - d.offY - parentRect.top) / parentRect.height;
    onChange?.({ x: clamp01(nx), y: clamp01(ny) });
  }, [onChange]);
  const handleEnd = useCallback(() => {
    dragRef.current.active = false;
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => handleMove(e.clientX, e.clientY);
    const onTouchMove = (e) => {
      const t = e.touches?.[0];
      if (t) handleMove(t.clientX, t.clientY);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('mouseup', handleEnd);
    window.addEventListener('touchend', handleEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('mouseup', handleEnd);
      window.removeEventListener('touchend', handleEnd);
    };
  }, [handleMove, handleEnd]);

  const sx = clamp01(x);
  const sy = clamp01(y);

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      data-testid={testId}
      onMouseDown={(e) => handleStart(e.clientX, e.clientY)}
      onTouchStart={(e) => {
        const t = e.touches?.[0];
        if (t) handleStart(t.clientX, t.clientY);
      }}
      className={`absolute ${locked ? '' : 'cursor-grab active:cursor-grabbing ring-1 ring-transparent hover:ring-primary/40'} transition`}
      style={{
        left: `${sx * 100}%`,
        top: `${sy * 100}%`,
        transform: 'translate(-50%, -50%)',
        width: sizePx,
        height: 'auto',
        touchAction: 'none',
      }}
    >
      {children}
    </div>
  );
}

function Slide({
  slide,
  idx,
  total,
  titleSize,
  bodySize,
  fontCss,
  logoDataUrl,
  brandLabel,
  authorName,
  templateKey,
  globalBgColor,
  logoPosition,
  onLogoMove,
  onImageMove,
  editing,
}) {
  const role = (slide?.role || 'default').toLowerCase();
  const palette = templateRoleColors(templateKey, role);
  const slideBg = slide?.background_color || globalBgColor || palette.bg;
  const isCover = role === 'cover';
  const img = slide?.image_data_url;
  const imgPos = slide?.image_position || DEFAULT_IMAGE_POSITION;
  const imgScale = imgPos.scale || 1;

  // ----- Custom layout dispatch -----
  // 6 templates (editorial_bold, index_card, portrait_spotlight, pop_frame,
  // magazine_mono, quote_stack) use bespoke PDF layouts on the backend, so
  // we render dedicated HTML/CSS approximations here. The draggable logo
  // still floats on top so users can reposition.
  if (isCustomLayout(templateKey)) {
    return (
      <div className="relative w-full">
        {renderCustomLayoutSlide({
          templateKey,
          slide,
          idx,
          total,
          palette: { ...palette, bg: slideBg },
          brandLabel,
          authorName: authorName || 'Author',
          testId: `carousel-slide-${idx}`,
        })}

        {/* Draggable logo overlay (preserved across all layouts) */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 pointer-events-auto">
            <Draggable
              x={logoPosition?.x ?? DEFAULT_LOGO_POSITION.x}
              y={logoPosition?.y ?? DEFAULT_LOGO_POSITION.y}
              sizePx={logoPosition?.size || DEFAULT_LOGO_POSITION.size}
              onChange={onLogoMove}
              locked={!editing}
              ariaLabel="Reposition logo"
              testId={`carousel-logo-drag-${idx}`}
            >
              {logoDataUrl ? (
                <img
                  src={logoDataUrl}
                  alt="logo"
                  className="object-contain w-full pointer-events-none"
                  style={{ height: (logoPosition?.size || DEFAULT_LOGO_POSITION.size) * 0.5 }}
                  draggable={false}
                />
              ) : null /* Brand label is already rendered by the layout itself */}
              {editing && (
                <span
                  className="absolute -top-2 -left-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px]"
                  aria-hidden
                >
                  <Move size={10} />
                </span>
              )}
            </Draggable>
          </div>
        </div>
      </div>
    );
  }

  // ----- Generic layout (default for the other 6 templates) -----
  // Image mode handling (classic templates now respect the same options as
  // custom layouts so the canvas preview matches the exported PDF):
  //   - 'free'        → legacy draggable 5:3 strip (existing behavior)
  //   - 'none'        → image hidden
  //   - 'full'        → full-bleed background image (with optional overlay)
  //   - 'vertical-band' → right-half side band
  //   - 'center-rect' → centered rectangle above the title block
  //   - 'circle'      → centered circular crop
  const imgMode = (slide?.image_mode || 'free').toLowerCase();
  const imgOpacity = typeof slide?.image_opacity === 'number' ? slide.image_opacity : 1;
  const imgOverlayColor = slide?.image_overlay_color || '#000000';
  const imgOverlayStrength = typeof slide?.image_overlay_strength === 'number'
    ? Math.max(0, Math.min(0.95, slide.image_overlay_strength))
    : 0;
  const useFreeImg = !!img && imgMode === 'free';
  const useFullImg = !!img && imgMode === 'full';
  const useBandImg = !!img && imgMode === 'vertical-band';
  const useRectImg = !!img && imgMode === 'center-rect';
  const useCircleImg = !!img && imgMode === 'circle';

  return (
    <div
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden border border-border select-none"
      style={{ fontFamily: fontCss, backgroundColor: slideBg }}
      data-testid={`carousel-slide-${idx}`}
    >
      {/* Full-bleed background image + optional overlay tint */}
      {useFullImg && (
        <>
          <img
            src={img}
            alt=""
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            style={{ opacity: imgOpacity }}
            draggable={false}
          />
          {imgOverlayStrength > 0 && (
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ backgroundColor: imgOverlayColor, opacity: imgOverlayStrength }}
            />
          )}
        </>
      )}

      {/* Vertical band image on the right half */}
      {useBandImg && (
        <div
          className="absolute right-0 top-0 h-full overflow-hidden pointer-events-none"
          style={{ width: '50%' }}
        >
          <img
            src={img}
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: imgOpacity }}
            draggable={false}
          />
          {imgOverlayStrength > 0 && (
            <div
              className="absolute inset-0"
              style={{ backgroundColor: imgOverlayColor, opacity: imgOverlayStrength }}
            />
          )}
        </div>
      )}

      {/* Centered rectangle image (sits above the title block) */}
      {useRectImg && (
        <div
          className="absolute rounded-lg overflow-hidden pointer-events-none"
          style={{
            left: '12%',
            right: '12%',
            top: '14%',
            aspectRatio: '5 / 3',
          }}
        >
          <img
            src={img}
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: imgOpacity }}
            draggable={false}
          />
          {imgOverlayStrength > 0 && (
            <div
              className="absolute inset-0"
              style={{ backgroundColor: imgOverlayColor, opacity: imgOverlayStrength }}
            />
          )}
        </div>
      )}

      {/* Circle image (centered at top) */}
      {useCircleImg && (
        <div
          className="absolute rounded-full overflow-hidden pointer-events-none"
          style={{
            left: '50%',
            transform: 'translateX(-50%)',
            top: '13%',
            width: '52%',
            aspectRatio: '1 / 1',
          }}
        >
          <img
            src={img}
            alt=""
            className="w-full h-full object-cover"
            style={{ opacity: imgOpacity }}
            draggable={false}
          />
          {imgOverlayStrength > 0 && (
            <div
              className="absolute inset-0"
              style={{ backgroundColor: imgOverlayColor, opacity: imgOverlayStrength }}
            />
          )}
        </div>
      )}

      {!isCover && (
        <div
          className="absolute left-0 top-12 bottom-12 w-1.5"
          style={{ backgroundColor: palette.accent }}
        />
      )}

      {useFreeImg && (
        <Draggable
          x={imgPos.x ?? 0.5}
          y={imgPos.y ?? 0.2}
          sizePx="80%"
          onChange={onImageMove}
          locked={!editing}
          ariaLabel="Reposition slide image"
          testId={`carousel-slide-image-drag-${idx}`}
        >
          <div
            className="rounded-lg overflow-hidden border border-white/10 shadow-md"
            style={{
              width: `${Math.min(100, 90 * imgScale)}%`,
              maxWidth: '100%',
              margin: '0 auto',
            }}
          >
            <img
              src={img}
              alt=""
              className="block w-full h-auto pointer-events-none"
              style={{ aspectRatio: '5 / 3', objectFit: 'cover', opacity: imgOpacity }}
              draggable={false}
            />
          </div>
          {editing && (
            <span
              className="absolute -top-2 -left-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px]"
              aria-hidden
            >
              <Move size={10} />
            </span>
          )}
        </Draggable>
      )}

      <div className="absolute top-5 left-7 right-7 flex items-center justify-between pointer-events-none">
        <span
          className="text-[10px] tracking-[0.18em]"
          style={{ fontFamily: '"Source Code Pro", monospace', color: palette.kicker }}
        >
          {slide?.kicker || `${String(idx + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`}
        </span>
      </div>

      {/* Logo / brand label — DRAGGABLE in edit mode */}
      <Draggable
        x={(logoPosition?.x ?? DEFAULT_LOGO_POSITION.x)}
        y={(logoPosition?.y ?? DEFAULT_LOGO_POSITION.y)}
        sizePx={logoPosition?.size || DEFAULT_LOGO_POSITION.size}
        onChange={onLogoMove}
        locked={!editing}
        ariaLabel="Reposition logo"
        testId={`carousel-logo-drag-${idx}`}
      >
        {logoDataUrl ? (
          <img
            src={logoDataUrl}
            alt="logo"
            className="object-contain w-full pointer-events-none"
            style={{ height: (logoPosition?.size || DEFAULT_LOGO_POSITION.size) * 0.5 }}
            draggable={false}
          />
        ) : (
          <span
            className="text-[10px] tracking-[0.18em] whitespace-nowrap pointer-events-none"
            style={{
              fontFamily: '"Source Code Pro", monospace',
              color: palette.kicker,
            }}
          >
            {(brandLabel || 'EXECPRESENCEAI').toUpperCase()}
          </span>
        )}
        {editing && (
          <span
            className="absolute -top-2 -left-2 inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px]"
            aria-hidden
          >
            <Move size={10} />
          </span>
        )}
      </Draggable>

      <div
        className={`absolute -translate-y-1/3 space-y-3 pointer-events-none ${
          useBandImg ? 'left-7 right-[55%]' : 'inset-x-7'
        } ${
          (useRectImg || useCircleImg) ? 'top-[62%]'
            : useFreeImg ? 'top-[60%]'
            : useFullImg ? 'top-[45%]'
            : 'top-1/3'
        }`}
        style={
          useFullImg
            ? {
                // Add a subtle text shadow so titles stay readable on full-bleed images
                textShadow: '0 2px 8px rgba(0,0,0,0.45)',
                color: '#FFFFFF',
              }
            : undefined
        }
      >
        <p
          className="font-bold leading-[1.05]"
          style={{
            fontSize: `${Math.max(14, Math.round((titleSize || 64) * 0.32))}px`,
            fontFamily: fontCss,
            color: useFullImg ? '#FFFFFF' : palette.title,
          }}
        >
          {slide?.title}
        </p>
        {slide?.body && (
          <p
            className="whitespace-pre-wrap leading-snug"
            style={{
              fontSize: `${Math.max(10, Math.round((bodySize || 36) * 0.32))}px`,
              fontFamily: fontCss,
              color: useFullImg ? 'rgba(255,255,255,0.92)' : palette.body,
            }}
          >
            {slide.body}
          </p>
        )}
      </div>

      <div
        className="absolute bottom-5 left-7 right-7 flex items-center justify-between text-[10px] pointer-events-none"
        style={{ fontFamily: '"Source Code Pro", monospace' }}
      >
        <span style={{ color: palette.kicker }}>{idx < total - 1 ? 'SWIPE \u2192' : ''}</span>
        <span style={{ color: palette.kicker }}>{role.toUpperCase()}</span>
      </div>
    </div>
  );
}

function TemplateTile({ tplKey, tpl, selected, onSelect }) {
  // Mini preview using cover colors
  const cover = tpl.cover;
  const useCustomMini = isCustomLayout(tplKey);
  const isPremium = !!tpl.premium;
  return (
    <button
      type="button"
      onClick={() => onSelect(tplKey)}
      data-testid={`carousel-template-${tplKey}`}
      className={`group rounded-lg overflow-hidden border text-left transition-all relative ${
        selected ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/50'
      }`}
    >
      {isPremium && (
        <span
          className="absolute top-1.5 right-1.5 z-10 px-1.5 py-0.5 rounded text-[8px] font-semibold tracking-[0.18em] uppercase"
          style={{ backgroundColor: 'rgba(0,0,0,0.78)', color: '#f7e26b' }}
        >
          Premium
        </span>
      )}
      {useCustomMini ? (
        <CustomLayoutMiniTile templateKey={tplKey} cover={cover} />
      ) : (
        <div
          className="aspect-[4/5] p-2 flex flex-col justify-between relative"
          // `background` (shorthand) so both solid colors AND CSS gradient
          // strings (premium templates) render correctly in the preview.
          style={{ background: cover.bg, color: cover.title }}
        >
          <span
            className="text-[8px] tracking-[0.18em]"
            style={{ fontFamily: '"Source Code Pro", monospace', color: cover.kicker }}
          >
            01 / 06
          </span>
          <div
            className="absolute left-0 top-6 bottom-6 w-1"
            style={{ backgroundColor: cover.accent }}
          />
          <div>
            <p className="text-[9px] font-bold leading-tight" style={{ color: cover.title }}>
              Cover headline
            </p>
            <p className="text-[7px] mt-0.5" style={{ color: cover.body }}>
              Subhead snippet
            </p>
          </div>
        </div>
      )}
      <div className="px-2 py-1.5 bg-card border-t border-border">
        <p className="text-[11px] font-medium truncate">{tpl.label}</p>
      </div>
    </button>
  );
}

export default function CarouselDialog({ open, onClose, analysisId, postIdx, post }) {
  const [carousel, setCarousel] = useState(post?.carousel || null);
  const [loading, setLoading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [active, setActive] = useState(0);
  const [editing, setEditing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const slideImgRef = useRef(null);
  const logoImgRef = useRef(null);

  const slides = carousel?.slides || [];
  const safeActive = Math.min(active, Math.max(0, slides.length - 1));
  const fontKey = carousel?.font_family || 'sans';
  const fontCss = (FONT_OPTIONS.find((f) => f.key === fontKey) || FONT_OPTIONS[0]).css;
  const titleSize = carousel?.title_size || 64;
  const bodySize = carousel?.body_size || 36;
  const templateKey = TEMPLATES[carousel?.template] ? carousel.template : 'bloomberg';
  const logoPosition = carousel?.logo_position || DEFAULT_LOGO_POSITION;
  const globalBg = carousel?.background_color || '';

  useEffect(() => {
    if (open && !carousel?.slides?.length && !loading) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    setCarousel(post?.carousel || null);
    setActive(0);
    setEditing(false);
    setDirty(false);
  }, [post?.carousel, postIdx]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await linkedge.getCarousel(analysisId, postIdx);
      setCarousel(data);
      setActive(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not build carousel');
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const data = await linkedge.regenerateCarousel(analysisId, postIdx);
      setCarousel(data);
      setActive(0);
      setDirty(false);
      toast.success('Carousel regenerated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not regenerate');
    } finally {
      setRegenerating(false);
    }
  };

  // ---- editor mutators ----
  const updateSlide = (i, patch) => {
    setCarousel((c) => ({
      ...(c || {}),
      slides: (c?.slides || []).map((s, idx) => (idx === i ? { ...s, ...patch } : s)),
    }));
    setDirty(true);
  };
  const updateConfig = (patch) => {
    setCarousel((c) => ({ ...(c || {}), ...patch }));
    setDirty(true);
  };

  const onPickSlideImage = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) {
      toast.error('Image too large (max 4MB)');
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    const existing = slides[safeActive] || {};
    updateSlide(safeActive, {
      image_data_url: dataUrl,
      image_position: existing.image_position || DEFAULT_IMAGE_POSITION,
      // Pick a sensible default layout on first upload so the image is immediately
      // prominent in the preview + PDF. Existing slides keep whatever mode they had.
      image_mode: existing.image_mode || 'center-rect',
    });
    toast.success('Slide image added — adjust layout, opacity & overlay in the panel');
  };
  const onPickLogo = async (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo too large (max 2MB)');
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    updateConfig({
      logo_data_url: dataUrl,
      logo_position: carousel?.logo_position || DEFAULT_LOGO_POSITION,
    });
    toast.success('Logo updated — drag to position, resize with slider');
  };

  const onLogoMove = ({ x, y }) => {
    updateConfig({
      logo_position: { ...(carousel?.logo_position || DEFAULT_LOGO_POSITION), x, y },
    });
  };
  const onLogoSize = (size) => {
    updateConfig({
      logo_position: { ...(carousel?.logo_position || DEFAULT_LOGO_POSITION), size },
    });
  };
  const onImageMove = ({ x, y }) => {
    const cur = slides[safeActive]?.image_position || DEFAULT_IMAGE_POSITION;
    updateSlide(safeActive, { image_position: { ...cur, x, y } });
  };
  const onImageScale = (scale) => {
    const cur = slides[safeActive]?.image_position || DEFAULT_IMAGE_POSITION;
    updateSlide(safeActive, { image_position: { ...cur, scale } });
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        slides: carousel.slides,
        theme: carousel.theme,
        template: templateKey,
        font_family: carousel.font_family || 'sans',
        title_size: carousel.title_size || null,
        body_size: carousel.body_size || null,
        logo_data_url: carousel.logo_data_url || null,
        logo_position: carousel.logo_position || null,
        brand_label: carousel.brand_label || null,
        background_color: carousel.background_color || null,
      };
      const data = await linkedge.saveCarousel(analysisId, postIdx, payload);
      setCarousel(data);
      setDirty(false);
      toast.success('Carousel saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    if (dirty) {
      await save();
    }
    setDownloading(true);
    try {
      await linkedge.downloadCarouselPdf(analysisId, postIdx);
      toast.success('Carousel PDF downloaded');
    } catch (_e) {
      toast.error('Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const currentSlide = slides[safeActive];
  const currentImgPos = currentSlide?.image_position || DEFAULT_IMAGE_POSITION;
  const currentSlideBg = currentSlide?.background_color || '';

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="carousel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers size={16} className="text-primary" />
            Carousel for post #{(postIdx ?? 0) + 1}
            {dirty && (
              <Badge className="ml-2 font-mono-edge text-[10px] bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30">
                UNSAVED
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Pick a template, drag logo &amp; images, recolor backgrounds, then download as a
            LinkedIn-ready PDF (1080&times;1350).
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-12 text-center" data-testid="carousel-loading">
            <Loader2 size={28} className="animate-spin text-primary mx-auto" />
            <p className="text-sm text-muted-foreground mt-3">Designing your carousel&hellip;</p>
          </div>
        )}

        {!loading && slides.length > 0 && (
          <div className="grid lg:grid-cols-[1fr_340px] gap-5">
            {/* LEFT — preview */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Badge className="font-mono-edge text-[10px]">
                  {slides.length} slides &middot; {TEMPLATES[templateKey].label}
                </Badge>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditing((e) => !e)}
                    data-testid="carousel-edit-toggle"
                  >
                    {editing ? <Eye size={14} className="mr-1.5" /> : <Pencil size={14} className="mr-1.5" />}
                    {editing ? 'Preview' : 'Edit'}
                  </Button>
                  <span className="text-[11px] font-mono-edge text-muted-foreground">
                    {String(safeActive + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
                  </span>
                </div>
              </div>

              <div className="flex justify-center">
                <div className="w-full max-w-xs">
                  <Slide
                    slide={currentSlide}
                    idx={safeActive}
                    total={slides.length}
                    titleSize={titleSize}
                    bodySize={bodySize}
                    fontCss={fontCss}
                    logoDataUrl={carousel?.logo_data_url}
                    brandLabel={carousel?.brand_label}
                    authorName={carousel?.author_name || post?.author_name || ''}
                    templateKey={templateKey}
                    globalBgColor={globalBg}
                    logoPosition={logoPosition}
                    onLogoMove={onLogoMove}
                    onImageMove={onImageMove}
                    editing={editing}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between gap-2 mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActive((a) => Math.max(0, a - 1))}
                  disabled={safeActive === 0}
                  data-testid="carousel-prev"
                >
                  <ChevronLeft size={14} className="mr-1" /> Prev
                </Button>
                <div className="flex items-center gap-1">
                  {slides.map((_s, i) => (
                    <button
                      key={i}
                      onClick={() => setActive(i)}
                      className={`h-1.5 rounded-full transition-all ${
                        i === safeActive ? 'w-6 bg-primary' : 'w-2 bg-secondary'
                      }`}
                      aria-label={`Go to slide ${i + 1}`}
                      data-testid={`carousel-dot-${i}`}
                    />
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setActive((a) => Math.min(slides.length - 1, a + 1))}
                  disabled={safeActive >= slides.length - 1}
                  data-testid="carousel-next"
                >
                  Next <ChevronRight size={14} className="ml-1" />
                </Button>
              </div>
              {editing && (
                <p className="text-[11px] text-muted-foreground mt-2 text-center">
                  <Move size={11} className="inline -mt-0.5 mr-1" />
                  Drag the logo or slide image directly on the canvas to reposition.
                </p>
              )}
            </div>

            {/* RIGHT — editor / inspector */}
            <div className="space-y-4">
              {editing ? (
                <>
                  {/* Template gallery */}
                  <div className="space-y-2 rounded-lg border border-border p-3" data-testid="carousel-template-gallery">
                    <div className="flex items-center gap-2">
                      <LayoutTemplate size={13} className="text-primary" />
                      <p className="text-[10px] font-mono-edge text-primary tracking-[0.16em]">
                        TEMPLATE
                      </p>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {TEMPLATES[templateKey].description}
                    </p>
                    <div className="grid grid-cols-3 gap-2 pt-1">
                      {Object.entries(TEMPLATES).map(([key, tpl]) => (
                        <TemplateTile
                          key={key}
                          tplKey={key}
                          tpl={tpl}
                          selected={key === templateKey}
                          onSelect={(k) => updateConfig({ template: k })}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Background color */}
                  <div className="space-y-3 rounded-lg border border-border p-3" data-testid="carousel-bg-editor">
                    <div className="flex items-center gap-2">
                      <Palette size={13} className="text-primary" />
                      <p className="text-[10px] font-mono-edge text-primary tracking-[0.16em]">
                        BACKGROUND COLOUR
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[11px]">This slide only</Label>
                      <ColorSwatchPicker
                        value={currentSlideBg || ''}
                        defaultValue={templateRoleColors(templateKey, currentSlide?.role || 'default').bg}
                        onChange={(hex) => updateSlide(safeActive, { background_color: hex || null })}
                        onClear={() => updateSlide(safeActive, { background_color: null })}
                        testIdPrefix="carousel-slide-bg"
                      />
                    </div>
                    <div className="space-y-2 pt-1 border-t border-border/60">
                      <Label className="text-[11px]">All slides (global)</Label>
                      <ColorSwatchPicker
                        value={globalBg || ''}
                        defaultValue={templateRoleColors(templateKey, 'default').bg}
                        onChange={(hex) => updateConfig({ background_color: hex || null })}
                        onClear={() => updateConfig({ background_color: null })}
                        testIdPrefix="carousel-global-bg"
                      />
                    </div>
                  </div>

                  {/* Per-slide text */}
                  <div className="space-y-3 rounded-lg border border-border p-3" data-testid="carousel-editor-slide">
                    <p className="text-[10px] font-mono-edge text-primary tracking-[0.16em]">SLIDE {safeActive + 1}</p>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Kicker</Label>
                      <Input
                        value={currentSlide?.kicker || ''}
                        onChange={(e) => updateSlide(safeActive, { kicker: e.target.value })}
                        placeholder="01 / 06"
                        data-testid="carousel-input-kicker"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Title</Label>
                      <Textarea
                        rows={2}
                        value={currentSlide?.title || ''}
                        onChange={(e) => updateSlide(safeActive, { title: e.target.value })}
                        data-testid="carousel-input-title"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Body</Label>
                      <Textarea
                        rows={4}
                        value={currentSlide?.body || ''}
                        onChange={(e) => updateSlide(safeActive, { body: e.target.value })}
                        data-testid="carousel-input-body"
                      />
                    </div>

                    {/* Slide image upload + drag/resize controls */}
                    <div className="space-y-2 pt-2 border-t border-border/60">
                      <Label className="text-xs">Slide image (drag on canvas to position)</Label>
                      <input
                        ref={slideImgRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onPickSlideImage(e.target.files?.[0])}
                        data-testid="carousel-slide-image-input"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => slideImgRef.current?.click()}
                          data-testid="carousel-slide-image-btn"
                        >
                          <ImageIcon size={13} className="mr-1.5" />
                          {currentSlide?.image_data_url ? 'Replace image' : 'Upload image'}
                        </Button>
                        {currentSlide?.image_data_url && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => updateSlide(safeActive, { image_data_url: null })}
                            data-testid="carousel-slide-image-clear"
                          >
                            <XCircle size={13} className="mr-1.5" /> Remove
                          </Button>
                        )}
                      </div>
                      {currentSlide?.image_data_url && (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between">
                            <Label className="text-[11px] text-muted-foreground">Image size</Label>
                            <span className="font-mono-edge text-[11px]">{Math.round((currentImgPos.scale || 1) * 100)}%</span>
                          </div>
                          <Slider
                            value={[Math.round((currentImgPos.scale || 1) * 100)]}
                            min={40}
                            max={200}
                            step={5}
                            onValueChange={(v) => onImageScale(v[0] / 100)}
                            data-testid="carousel-slide-image-scale"
                          />
                        </div>
                      )}
                      {currentSlide?.image_data_url && (
                        <div className="space-y-2 pt-1 border-t border-border/60">
                          {/* Image layout mode */}
                          <div className="space-y-1.5">
                            <Label className="text-[11px] text-muted-foreground" htmlFor="carousel-slide-image-mode">
                              Image layout
                            </Label>
                            <select
                              id="carousel-slide-image-mode"
                              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs"
                              value={currentSlide?.image_mode || 'free'}
                              onChange={(e) => updateSlide(safeActive, { image_mode: e.target.value })}
                              data-testid="carousel-slide-image-mode"
                            >
                              <option value="free">Free position (legacy)</option>
                              <option value="none">None / hide image</option>
                              <option value="vertical-band">Vertical band (side strip)</option>
                              <option value="center-rect">Center rectangle</option>
                              <option value="circle">Circle</option>
                              <option value="full">Full bleed background</option>
                            </select>
                          </div>
                          {/* Opacity slider */}
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <Label className="text-[11px] text-muted-foreground">Image opacity</Label>
                              <span className="font-mono-edge text-[11px]">
                                {Math.round((currentSlide?.image_opacity ?? 1) * 100)}%
                              </span>
                            </div>
                            <Slider
                              value={[Math.round((currentSlide?.image_opacity ?? 1) * 100)]}
                              min={10}
                              max={100}
                              step={5}
                              onValueChange={(v) => updateSlide(safeActive, { image_opacity: v[0] / 100 })}
                              data-testid="carousel-slide-image-opacity"
                            />
                          </div>
                          {/* Overlay color + strength */}
                          <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                              <Label className="text-[11px] text-muted-foreground">Overlay colour</Label>
                              <span className="font-mono-edge text-[11px]">
                                {Math.round((currentSlide?.image_overlay_strength ?? 0) * 100)}%
                              </span>
                            </div>
                            <ColorSwatchPicker
                              value={currentSlide?.image_overlay_color || '#000000'}
                              defaultValue="#000000"
                              onChange={(hex) =>
                                updateSlide(safeActive, { image_overlay_color: hex || '#000000' })
                              }
                              testIdPrefix="carousel-slide-image-overlay"
                              compact
                            />
                          </div>
                          <Slider
                            value={[Math.round((currentSlide?.image_overlay_strength ?? 0) * 100)]}
                            min={0}
                            max={80}
                            step={5}
                            onValueChange={(v) => updateSlide(safeActive, { image_overlay_strength: v[0] / 100 })}
                            data-testid="carousel-slide-image-overlay-strength"
                          />
                          <p className="text-[10px] text-muted-foreground leading-relaxed">
                            Layout, opacity &amp; overlay now apply to <span className="font-medium text-foreground">all templates</span> — both classic and custom layouts — in the live preview and the exported PDF.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Global config */}
                  <div className="space-y-3 rounded-lg border border-border p-3" data-testid="carousel-editor-global">
                    <p className="text-[10px] font-mono-edge text-primary tracking-[0.16em]">DESIGN</p>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Font family</Label>
                      <Select
                        value={fontKey}
                        onValueChange={(v) => updateConfig({ font_family: v })}
                      >
                        <SelectTrigger data-testid="carousel-font-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONT_OPTIONS.map((f) => (
                            <SelectItem key={f.key} value={f.key} data-testid={`carousel-font-${f.key}`}>
                              <span style={{ fontFamily: f.css }}>{f.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs flex items-center gap-1">
                          <TypeIcon size={11} /> Title size
                        </Label>
                        <span className="font-mono-edge text-xs">{titleSize}</span>
                      </div>
                      <Slider
                        value={[titleSize]}
                        min={40}
                        max={96}
                        step={2}
                        onValueChange={(v) => updateConfig({ title_size: v[0] })}
                        data-testid="carousel-title-size"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">Body size</Label>
                        <span className="font-mono-edge text-xs">{bodySize}</span>
                      </div>
                      <Slider
                        value={[bodySize]}
                        min={20}
                        max={56}
                        step={2}
                        onValueChange={(v) => updateConfig({ body_size: v[0] })}
                        data-testid="carousel-body-size"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Brand label (used when no logo)</Label>
                      <Input
                        value={carousel.brand_label || ''}
                        placeholder="EXECPRESENCEAI"
                        onChange={(e) => updateConfig({ brand_label: e.target.value })}
                        data-testid="carousel-brand-label"
                      />
                    </div>
                    <div className="space-y-2 pt-2 border-t border-border/60">
                      <Label className="text-xs">Logo (drag on canvas, resize below)</Label>
                      <input
                        ref={logoImgRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => onPickLogo(e.target.files?.[0])}
                        data-testid="carousel-logo-input"
                      />
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => logoImgRef.current?.click()}
                          data-testid="carousel-logo-btn"
                        >
                          <ImageIcon size={13} className="mr-1.5" />
                          {carousel.logo_data_url ? 'Replace logo' : 'Upload logo'}
                        </Button>
                        {carousel.logo_data_url && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => updateConfig({ logo_data_url: null })}
                            data-testid="carousel-logo-clear"
                          >
                            <XCircle size={13} className="mr-1.5" /> Remove
                          </Button>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Label className="text-[11px] text-muted-foreground">Logo size</Label>
                          <span className="font-mono-edge text-[11px]">{logoPosition.size || 56}px</span>
                        </div>
                        <Slider
                          value={[logoPosition.size || 56]}
                          min={24}
                          max={200}
                          step={2}
                          onValueChange={(v) => onLogoSize(v[0])}
                          data-testid="carousel-logo-size"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          updateConfig({ logo_position: { ...DEFAULT_LOGO_POSITION } })
                        }
                        data-testid="carousel-logo-reset"
                        className="text-xs"
                      >
                        Reset logo position
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Read-only thumbs */}
                  <div className="grid grid-cols-4 gap-2" data-testid="carousel-thumbs">
                    {slides.map((s, i) => {
                      const isActive = i === safeActive;
                      const role = (s?.role || 'insight').toLowerCase();
                      const palette = templateRoleColors(templateKey, role);
                      return (
                        <button
                          key={i}
                          onClick={() => setActive(i)}
                          className={`relative rounded-lg overflow-hidden border text-left aspect-[4/5] flex flex-col justify-between p-2 transition-colors ${
                            isActive ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary/40'
                          }`}
                          style={{ backgroundColor: s?.background_color || globalBg || palette.bg }}
                          data-testid={`carousel-thumb-${i}`}
                        >
                          <span
                            className="text-[9px] font-mono-edge tracking-wide"
                            style={{ color: palette.kicker }}
                          >
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span
                            className="text-[9px] font-mono-edge uppercase tracking-wide"
                            style={{ color: palette.title }}
                          >
                            {role}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Click <span className="font-medium text-foreground">Edit</span> to pick a template,
                    drag the logo or images, change background colour, or tune fonts.
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="flex-row gap-2 justify-end">
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {slides.length > 0 && (
            <>
              {editing && (
                <Button
                  variant="secondary"
                  onClick={save}
                  disabled={saving || !dirty}
                  data-testid="carousel-save"
                >
                  {saving ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
                  Save
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={regenerate}
                disabled={regenerating}
                data-testid="carousel-regenerate"
              >
                {regenerating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <RefreshCcw size={14} className="mr-1.5" />}
                Regenerate
              </Button>
              <Button onClick={download} disabled={downloading} data-testid="carousel-download-pdf">
                {downloading ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
                Download PDF
              </Button>
            </>
          )}
          {!loading && slides.length === 0 && (
            <Button onClick={load} data-testid="carousel-generate-cta">
              <Sparkles size={14} className="mr-1.5" /> Build carousel
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
